<app-sidebar>
  <div class="feature-flags-page">
    <div class="page-header">
      <h1>Feature Flags</h1>
      <p *ngIf="isPlatformMode">Manage organization defaults and user overrides across tenants.</p>
      <p *ngIf="!isPlatformMode">Manage user overrides for your organization. Organization defaults are controlled by platform admin.</p>
    </div>

    <div *ngIf="loadingRegistry" class="feature-flags-loading-state">
      <mat-spinner diameter="50"></mat-spinner>
      <p>Loading feature flags...</p>
    </div>

    <div class="feature-flags-card" *ngIf="!loadingRegistry">
      <div class="controls-grid">
        <mat-form-field appearance="outline">
          <mat-label>Flag</mat-label>
          <mat-select [(ngModel)]="selectedFlagKey" (selectionChange)="onFlagChanged()">
            <mat-option *ngFor="let flag of registry" [value]="flag.key">
              {{ flag.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" *ngIf="showOrgScope || availableScopes.length > 1">
          <mat-label>Scope</mat-label>
          <mat-select
            [(ngModel)]="selectedScope"
            (selectionChange)="onScopeChanged()"
            [disabled]="selectedFlag?.key === 'masquerade_as'">
            <mat-option *ngIf="availableScopes.includes('org')" value="org">Organization</mat-option>
            <mat-option *ngIf="availableScopes.includes('user')" value="user">User</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" *ngIf="isPlatformMode && showOrgTarget">
          <mat-label>Organization</mat-label>
          <mat-select [(ngModel)]="selectedTargetId" (selectionChange)="onTargetChanged()">
            <mat-option *ngFor="let org of organizations" [value]="org.orgId">
              {{ org.displayName }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" *ngIf="isPlatformMode && showUserTarget">
          <mat-label>Organization filter</mat-label>
          <mat-select [(ngModel)]="selectedOrgFilterId" (selectionChange)="onOrgFilterChanged()">
            <mat-option value="">All organizations</mat-option>
            <mat-option *ngFor="let org of organizations" [value]="org.orgId">
              {{ org.displayName }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" *ngIf="showUserTarget">
          <mat-label>Filter users</mat-label>
          <input
            matInput
            [(ngModel)]="userSearch"
            (ngModelChange)="onUserSearchChanged()"
            placeholder="Search name or email">
        </mat-form-field>

        <mat-form-field appearance="outline" *ngIf="showUserTarget">
          <mat-label>User</mat-label>
          <mat-select
            [(ngModel)]="selectedTargetId"
            (selectionChange)="onTargetChanged()"
            [disabled]="loadingTargets || (isPlatformMode && !selectedOrgFilterId && filteredUsers.length === 0)">
            <mat-option *ngFor="let user of filteredUsers" [value]="user.userId">
              {{ userLabel(user) }}
            </mat-option>
          </mat-select>
          <mat-hint *ngIf="isPlatformMode && !selectedOrgFilterId">
            Select an organization filter to load users
          </mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>State</mat-label>
          <mat-select [(ngModel)]="selectedState" (selectionChange)="onStateChanged()">
            <mat-option value="enabled">Enabled</mat-option>
            <mat-option value="disabled">Disabled</mat-option>
            <mat-option *ngIf="allowInherit" value="inherit">Inherit from org</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="confirmation-box" *ngIf="confirmationMessage">
        {{ confirmationMessage }}
      </div>

      <div class="apply-row">
        <button
          mat-raised-button
          color="primary"
          type="button"
          [disabled]="!canApply"
          (click)="apply()">
          <mat-icon>check</mat-icon>
          Apply
        </button>
        <mat-spinner *ngIf="applying || loadingTargets" diameter="24"></mat-spinner>
      </div>
    </div>

    <div class="feature-flags-card" *ngIf="!loadingRegistry && selectedFlagKey">
      <div class="section-header">
        <h2>Current assignments</h2>
        <mat-spinner *ngIf="loadingAssignments" diameter="24"></mat-spinner>
      </div>

      <p class="empty-message" *ngIf="!loadingAssignments && assignmentRows.data.length === 0">
        No assignments for this flag{{ isPlatformMode && selectedOrgFilterId ? ' in the selected organization' : '' }}.
      </p>

      <div class="table-shell" *ngIf="!loadingAssignments && assignmentRows.data.length > 0">
        <mat-table [dataSource]="assignmentRows">
          <ng-container matColumnDef="scope">
            <mat-header-cell *matHeaderCellDef>Scope</mat-header-cell>
            <mat-cell *matCellDef="let row">{{ row.scope === 'org' ? 'Organization' : 'User' }}</mat-cell>
          </ng-container>

          <ng-container matColumnDef="target">
            <mat-header-cell *matHeaderCellDef>Target</mat-header-cell>
            <mat-cell *matCellDef="let row">{{ row.targetLabel }}</mat-cell>
          </ng-container>

          <ng-container matColumnDef="organization">
            <mat-header-cell *matHeaderCellDef>Organization</mat-header-cell>
            <mat-cell *matCellDef="let row">{{ row.organizationLabel }}</mat-cell>
          </ng-container>

          <ng-container matColumnDef="state">
            <mat-header-cell *matHeaderCellDef>State</mat-header-cell>
            <mat-cell *matCellDef="let row">
              <mat-chip [color]="row.enabled ? 'primary' : 'warn'">
                {{ formatState(row.enabled) }}
              </mat-chip>
            </mat-cell>
          </ng-container>

          <ng-container matColumnDef="updatedAt">
            <mat-header-cell *matHeaderCellDef>Updated</mat-header-cell>
            <mat-cell *matCellDef="let row">{{ formatDate(row.updatedAt) }}</mat-cell>
          </ng-container>

          <ng-container matColumnDef="actions">
            <mat-header-cell *matHeaderCellDef>Actions</mat-header-cell>
            <mat-cell *matCellDef="let row" class="action-cell">
              <button
                mat-icon-button
                type="button"
                color="warn"
                matTooltip="Clear user override (inherit)"
                *ngIf="row.scope === 'user'"
                (click)="clearUserOverride(row)">
                <mat-icon>remove_circle_outline</mat-icon>
              </button>
            </mat-cell>
          </ng-container>

          <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
          <mat-row *matRowDef="let row; columns: displayedColumns"></mat-row>
        </mat-table>
      </div>
    </div>
  </div>
</app-sidebar>
